import { useAtom } from "jotai";
import { useEffect } from "react";
import { toast } from "sonner";

import { brandDealAtom } from "../atoms/brandDealAtom";
import { BrandDeal } from "@quantizr-engineering/common/types/brand-deal";
import { FIVE_MINUTES_MS } from "../utils/consts";
import apiClient from "./apiClient";

export const useBrandDealService = () => {
  const [brandDeals, setBrandDeals] = useAtom(brandDealAtom);

  useEffect(() => {
    cacheGetBrandDeals();
  }, []);

  const getBrandDeals = async () => {
    setBrandDeals((prev) => ({ ...prev, loading: true }));

    try {
      const response = await apiClient.get("/brand-deal", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      setBrandDeals((prev) => ({
        loading: false,
        data: response.data,
        lastFetched: Date.now(),
      }));
      return response.data;
    } catch (error) {
      setBrandDeals((prev) => ({
        ...prev,
        loading: false,
        data: [],
        lastFetched: null,
      }));
      throw error;
    }
  };

  const cacheGetBrandDeals = async () => {
    if (!!brandDeals.loading) {
      return;
    }
    if (
      !brandDeals.lastFetched ||
      Date.now() - brandDeals.lastFetched > FIVE_MINUTES_MS
    ) {
      await getBrandDeals();
    }
  };

  const postBrandDeal = async (brandDeal: BrandDeal) => {
    try {
      const response = await apiClient.post("/brand-deal", brandDeal, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      await getBrandDeals();
      toast.success("Brand deal added");
      return response;
    } catch (error) {
      toast.error("Failed to add brand deal");
      throw error;
    }
  };

  const patchBrandDeal = async (brandDeal: BrandDeal) => {
    try {
      const response = await apiClient.patch(
        `/brand-deal/${brandDeal.id}`,
        brandDeal,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      await getBrandDeals();
      toast.success("Updated brand deal");
      return response;
    } catch (error) {
      toast.error("Failed to update brand deal");
      throw error;
    }
  };

  const deleteBrandDeal = async (brandDealId: number) => {
    try {
      const response = await apiClient.delete(`/brand-deal/${brandDealId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      await getBrandDeals();
      toast.success("Brand deal deleted");
      return response;
    } catch (error) {
      toast.error("Failed to delete brand deal");
      throw error;
    }
  };

  return {
    brandDeals: brandDeals.data,
    brandDealsLoading: brandDeals.loading,
    getBrandDeals,
    cacheGetBrandDeals,
    postBrandDeal,
    patchBrandDeal,
    deleteBrandDeal,
  };
};
